/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  AlumniRequest,
  AlumniUploadCsvRequest,
  AlumniUploadCsvResponse,
  Disable2FARequest,
  Disable2FAResponse,
  EmailResponse,
  FolioAccountVerificationRequest,
  PersonalDataRequest,
  Submit2FARequest,
  TokenVerification,
  TwoFactorSecretResponse,
  TwoFactorStatusResponse,
  UserNotificationsSettingsRequest,
  UserPrivacySettingsRequest,
  UsersUploadCsvRequest,
  UsersUploadCsvResponse,
  Validate2FARequest,
} from '../../../shared/models/account/account.model';
import { DEFAULT_LANGUAGE_CODE } from '../../../shared/services/languages/language.service';
import { ObservableResult } from '../../../shared/store';
import { UserDetailsSummary, UserSession } from '../../../user-auth/models';
import { AccountDataService } from './account-data.service';

@Injectable()
export class MockAccountDataService implements AccountDataService {
  private personalData: UserDetailsSummary = {
    uid: 'userUid',
    email: 'some@emai.com',
    firstName: 'User first name',
    lastName: 'User last name',
    imageUrl: 'assets/profile_image.png',
    eptid: '1231qbwer2312b3452234',
    description: [
      {
        languageCode: DEFAULT_LANGUAGE_CODE,
        value: 'Who I am?',
      },
    ],
    recoveryEmail: 'recovery@email.com',
    userSettings: {
      privacy: {
        folioPrivacyMode: undefined,
        folioCommentsMode: undefined,
      },
      notifications: {
        eventEmailSettings: {
          enabled: undefined,
          groups: undefined,
        },
        periodicEmailSettings: {
          enabled: undefined,
          contents: undefined,
        },
      },
      language: DEFAULT_LANGUAGE_CODE,
    },
    roles: [
      {
        type: 'SUPER_ADMIN',
        _id: '123123',
        name: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: 'name' }],
        privileges: [
          {
            assigned: true,
            name: 'some-name',
            _id: 'someId',
            label: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: 'label' }],
            privilegeGroup: 'SUPER_ADMIN',
          },
        ],
      },
      {
        type: 'ADMIN',
        _id: '123123',
        name: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: 'name' }],
        privileges: [
          {
            assigned: true,
            name: 'some-name',
            _id: 'someId',
            label: [{ languageCode: DEFAULT_LANGUAGE_CODE, value: 'label' }],
            privilegeGroup: 'ADMIN',
          },
        ],
      },
    ],
    groups: [
      {
        groupUid: '123',
        addedOn: new Date(),
      },
    ],
    workspaces: [
      {
        name: 'Lboro',
        domain: 'lboro.yuna.dev.potential.ly',
        logo: '',
        site: 'lboro.yuna.dev.potential.ly',
        organizationUid: 'organizationUid',
        type: 'EDUCATIONAL',
        memberDetails: {
          email: 'admin@potantial.ly',
          firstName: 'Admin',
          lastName: 'Potentially',
          imageUrl: '',
          status: 'ACTIVE',
          hasUsedCpd: false,
          isCpdConnected: false,
        },
      },
    ],
    organization: { _id: 'org', name: 'Lboro' },
  };

  getPersonalData(): ObservableResult<UserDetailsSummary> {
    return ObservableResult.ofSuccess(this.personalData);
  }

  updatePersonalData(userData: PersonalDataRequest): ObservableResult<UserDetailsSummary> {
    return ObservableResult.ofSuccess();
  }

  resetUserPassword(): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  resetPasswordByEmail(email: string, captchaResponseV3?: string, captchaResponseV2?: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  resetUserEmail(): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getEmailByToken(tokenVerification: TokenVerification): ObservableResult<EmailResponse> {
    return ObservableResult.ofSuccess();
  }

  resetUserRecoveryEmail(email: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateUserPassword(password: string, captchaResponseV3?: string, captchaResponseV2?: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateUserEmail(email: string, captchaResponseV3?: string, captchaResponseV2?: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateUserRecoveryEmail(token: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateUserPrivacy(request: UserPrivacySettingsRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateUserNotifications(request: UserNotificationsSettingsRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  connectUserToWorkspace(email: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  connectUniversityEmail(universityEmail: string, folioAccountInitialization: boolean): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  leaveUserFromWorkspace(organizationUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  verifyWorkspaceEmail(request: FolioAccountVerificationRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  confirmWorkspaceEmailWithPassword(request: FolioAccountVerificationRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  alumniRequest(request: AlumniRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  alumniUploadCsv(request: AlumniUploadCsvRequest): ObservableResult<AlumniUploadCsvResponse> {
    return ObservableResult.ofSuccess();
  }

  usersUploadCsv(request: UsersUploadCsvRequest): ObservableResult<UsersUploadCsvResponse> {
    return ObservableResult.ofSuccess();
  }

  setLanguage(code: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateJiscUsage(hasUsedCpd: boolean): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getUserSessions(): Observable<UserSession[]> {
    return of([]);
  }

  // Simple in-memory mock state so the v3 flows (enable/reconfigure/disable) are exercisable end-to-end
  // against the mock service while the real backend prerequisites (see plan) aren't in place yet.
  private mock2FAEnabled = false;

  get2FAStatus(): ObservableResult<TwoFactorStatusResponse> {
    return ObservableResult.ofSuccess({
      enabled: this.mock2FAEnabled,
      bypass: false,
      reason: null,
    });
  }

  generate2FASecret(): ObservableResult<TwoFactorSecretResponse> {
    return ObservableResult.ofSuccess({
      encodedTotpSecret: 'MOCK_SECRET_KEY_123456',
      totpSecretQRCode:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
    });
  }

  submit2FA(request: Submit2FARequest): ObservableResult<void> {
    this.mock2FAEnabled = true;
    return ObservableResult.ofSuccess();
  }

  validate2FACode(request: Validate2FARequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  disable2FA(request?: Disable2FARequest): ObservableResult<Disable2FAResponse> {
    this.mock2FAEnabled = false;
    return ObservableResult.ofSuccess({
      message: 'Two-factor authentication disabled successfully',
      enabled: false,
      userId: 'mock-user-id',
      code: 'SUCCESS',
    });
  }
}
